import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import Dashboard from './pages/Dashboard';
import Resources from './pages/Resources';
import ResourceDetail from './pages/ResourceDetail';
import Providers from './pages/Providers';
import Analytics from './pages/Analytics';
import Alerts from './pages/Alerts';
import CreateResource from './pages/CreateResource';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AcceptInvitation from './pages/AcceptInvitation';
import SecuritySettings from './pages/SecuritySettings';
import SshIdentitySettings from './pages/SshIdentitySettings';
import PeopleAccess from './pages/PeopleAccess';
import MyWork from './pages/MyWork';
import { api } from './utils/api';
import { ScopeProvider, useScope } from './contexts/ScopeContext';
import './App.css';

const ProtectedRoute = ({ user, children }) => {
  const scope = useScope();
  if (!user) return <Navigate to="/" />;
  if (scope.status === 'loading' || scope.status === 'idle') {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400" role="status">Loading your project…</div>;
  }
  if (scope.status === 'error') {
    return (
      <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center gap-4 px-6 text-center" role="alert">
        <p className="text-zinc-200">{scope.error}</p>
        <button onClick={scope.retry} className="rounded-lg bg-amber-500 px-4 py-2 font-medium text-zinc-950">Try again</button>
      </div>
    );
  }
  if (scope.status === 'empty') {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6 text-center">
        <div><h1 className="text-xl font-semibold text-zinc-100">No project access</h1><p className="mt-2 text-zinc-400">Ask an organization administrator to add you to a project.</p></div>
      </div>
    );
  }
  return <React.Fragment key={scope.version}>{children}</React.Fragment>;
};

const AuthenticatedRoute = ({ user, children }) => user ? children : <Navigate to="/" />;

const NotFound = ({ user }) => (
  <main className="min-h-screen bg-zinc-950 px-6 text-zinc-100 flex items-center justify-center text-center">
    <div>
      <p className="text-sm font-medium text-amber-400">404</p>
      <h1 className="mt-2 text-2xl font-semibold">Page not found</h1>
      <p className="mt-2 text-zinc-400">This address does not match a Cloud Watcher page.</p>
      <Link to={user ? '/dashboard' : '/'} className="mt-5 inline-flex min-h-11 items-center rounded-lg bg-amber-500 px-4 font-medium text-zinc-950">
        {user ? 'Go to dashboard' : 'Go home'}
      </Link>
    </div>
  </main>
);

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    api.getSession()
      .then(response => setUser(response.data.user))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => setUser(null);
    window.addEventListener('cloud-watcher-session-expired', handleSessionExpired);
    return () => window.removeEventListener('cloud-watcher-session-expired', handleSessionExpired);
  }, []);

  const login = (user) => {
    setUser(user);
  };

  const logout = async () => {
    try { await api.logout(); } catch (_) { /* clear local session state regardless */ }
    setUser(null);
  };

  if (authLoading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400" role="status"><span className="sr-only">Loading session.</span>Loading Cloud Watcher…</div>;

  return (
    <div className="App">
      <BrowserRouter>
        <ScopeProvider enabled={Boolean(user)}>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
          <Route path="/auth" element={!user ? <AuthPage onLogin={login} /> : <Navigate to="/dashboard" />} />
          <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
          <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" />} />
          <Route path="/accept-invitation" element={!user ? <AcceptInvitation /> : <Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/resources" element={<ProtectedRoute user={user}><Resources user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/resources/create" element={<ProtectedRoute user={user}><CreateResource user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/resources/:id" element={<ProtectedRoute user={user}><ResourceDetail user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/providers" element={<ProtectedRoute user={user}><Providers user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute user={user}><Analytics user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute user={user}><Alerts user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/settings/security" element={<ProtectedRoute user={user}><SecuritySettings user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/settings/ssh-identity" element={<AuthenticatedRoute user={user}><SshIdentitySettings user={user} onLogout={logout} /></AuthenticatedRoute>} />
          <Route path="/settings/access" element={<ProtectedRoute user={user}><PeopleAccess user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="/work" element={<ProtectedRoute user={user}><MyWork user={user} onLogout={logout} /></ProtectedRoute>} />
          <Route path="*" element={<NotFound user={user} />} />
        </Routes>
        </ScopeProvider>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
